import { registerUploadFilesApi, getUploadFileUrlsApi, getUploadPartUrlsApi, updateUploadFileStatusApi, restartUploadFileApi, reportUploadFileProgressApi, normalizeUploadPath, UPLOAD_TYPE } from './apiServices';
import { updateLiveUpload, withUploadLock, waitForOnline, LIVE_STATUS } from './liveUploads';

// Files are registered and signed one batch at a time so signed URLs are fresh when used
// and a 10k-file upload never fires thousands of API calls at once
const BATCH_SIZE = 500;
const UPLOAD_CONCURRENCY = 6;
// Large (multipart) files: parts uploaded in parallel per file, and part URLs signed in
// small groups so they don't expire before use
const PART_CONCURRENCY = 4;
const PART_SIGN_BATCH = 20;
const MAX_ATTEMPTS = 3;
// A storage PUT that sends nothing for this long is treated as a dead connection, aborted and retried
const STALL_TIMEOUT_MS = 60 * 1000;
// The complete call can report missing parts; re-upload them at most this many times
const MAX_COMPLETE_ROUNDS = 3;
// Refresh a signed URL this long before its expires_at rather than letting S3 reject it
const URL_EXPIRY_MARGIN_MS = 60 * 1000;
// Register statuses meaning the file is already uploaded in this session
const DONE_STATUSES = ['COMPLETED', 'UPLOADED'];
// Byte progress fires many times a second; publish to the UI at most this often
const PUBLISH_INTERVAL_MS = 300;
// Speed is smoothed over samples at least this far apart so it doesn't jitter
const SPEED_SAMPLE_MS = 1000;
// Bytes go straight to storage, so without a heartbeat the server can go minutes without hearing from
// an upload that is still running. Each in-flight file reports its progress this often, which keeps
// its updated_at fresh for other tabs, devices and users.
const HEARTBEAT_INTERVAL_MS = 30 * 1000;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Toast text for a finished run: { message, variant }. Failed and skipped files carry their reasons,
 * so the most common one is shown (e.g. "Folder does not exist!") instead of only a count.
 */
export const describeUploadResult = (result) => {
  if (result.alreadyRunning) return { message: 'This upload is already running in another tab', variant: 'warning' };
  if (!result.failedFiles && !result.skippedFiles) {
    return { message: `${result.completedFiles.toLocaleString()} file${result.completedFiles === 1 ? '' : 's'} uploaded successfully`, variant: 'success' };
  }
  const parts = [`${result.completedFiles.toLocaleString()} uploaded`];
  if (result.failedFiles) parts.push(`${result.failedFiles.toLocaleString()} failed`);
  if (result.skippedFiles) parts.push(`${result.skippedFiles.toLocaleString()} skipped`);
  const counts = new Map();
  [...result.failures.map(entry => entry.error), ...result.skipped.map(entry => entry.reason)]
    .filter(Boolean)
    .forEach(reason => counts.set(reason, (counts.get(reason) || 0) + 1));
  const reasons = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map(([reason]) => reason);
  let detail = '';
  if (reasons.length === 1) detail = ` (${reasons[0]})`;
  else if (reasons.length > 1) detail = ` (${reasons[0]}, and ${reasons.length - 1} other reason${reasons.length > 2 ? 's' : ''})`;
  return { message: `Upload finished: ${parts.join(', ')}${detail}`, variant: result.completedFiles ? 'warning' : 'error' };
};

// Size of part n (1-based) of a multipart file; the last part is usually shorter
const partSizeOf = (file, partSize, n) => Math.max(0, Math.min(partSize, file.size - (n - 1) * partSize));

const missingPartsOf = (error) => (error && error.data && Array.isArray(error.data.missing_parts) ? error.data.missing_parts.map(Number) : []);

// Exponential backoff with jitter: ~1s, ~2s, ~4s. A missing_parts answer isn't transient,
// so it's handed straight back to the caller instead of being retried.
// While the browser is offline nothing is attempted: the call waits for the connection to come back,
// and a failure caused by going offline doesn't use up an attempt. Without this a short outage would
// burn every retry in seconds and fail whole batches of files.
// The same goes for a paused run: nothing starts until it's resumed, and transfers cancelled by the
// pause are simply tried again afterwards.
const withRetry = async (task, control) => {
  let lastError;
  let attempt = 0;
  while (attempt < MAX_ATTEMPTS) {
    if (control) await untilRunning(control);
    await waitForOnline();
    try {
      return await task(attempt);
    } catch (error) {
      lastError = error;
      if (missingPartsOf(error).length) throw error;
      if (error.paused || (control && control.paused)) continue;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) continue;
      attempt += 1;
      if (attempt < MAX_ATTEMPTS) await sleep(1000 * 2 ** (attempt - 1) + Math.random() * 300);
    }
  }
  throw lastError;
};

// Pause / resume for runs in this tab, keyed by upload_id. Pausing cancels the transfers in progress
// (a large file keeps the parts it already finished) and holds every next step until resumed.
const controls = new Map();
const createControl = (uploadId) => {
  const control = { paused: false, waiters: [], xhrs: new Set() };
  controls.set(uploadId, control);
  return control;
};
const untilRunning = (control) => (control.paused ? new Promise(resolve => control.waiters.push(resolve)) : Promise.resolve());

export const pauseStreamUpload = (uploadId) => {
  const control = controls.get(uploadId);
  if (!control || control.paused) return false;
  control.paused = true;
  control.xhrs.forEach(xhr => {
    xhr.pausedAbort = true;
    xhr.abort();
  });
  updateLiveUpload(uploadId, { status: LIVE_STATUS.PAUSED, speed: 0 });
  return true;
};

export const resumeStreamUpload = (uploadId) => {
  const control = controls.get(uploadId);
  if (!control || !control.paused) return false;
  control.paused = false;
  control.waiters.splice(0).forEach(resolve => resolve());
  updateLiveUpload(uploadId, { status: LIVE_STATUS.UPLOADING });
  return true;
};

const isExpiring = (signed) => !!signed.expiresAt && Date.now() > signed.expiresAt - URL_EXPIRY_MARGIN_MS;

// XHR rather than fetch because only XHR reports upload progress. It also never carries the
// app's Authorization header, which S3 rejects on presigned requests.
// A connection that dies mid-upload may never error out, so the PUT is aborted once it has sent
// nothing for STALL_TIMEOUT_MS (rather than a fixed timeout, which a slow but healthy large part could hit).
const putToStorage = ({ url, method = 'PUT', headers = {} }, body, onBytes, control) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  if (control) control.xhrs.add(xhr);
  let lastActivity = Date.now();
  let stalled = false;
  const watchdog = setInterval(() => {
    if (Date.now() - lastActivity > STALL_TIMEOUT_MS) {
      stalled = true;
      xhr.abort();
    }
  }, 5000);
  const settle = (fn, value) => {
    clearInterval(watchdog);
    if (control) control.xhrs.delete(xhr);
    fn(value);
  };
  xhr.open(method, url);
  Object.keys(headers).forEach(name => xhr.setRequestHeader(name, headers[name]));
  xhr.upload.onprogress = (evt) => {
    lastActivity = Date.now();
    onBytes(evt.loaded);
  };
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      settle(resolve);
    } else {
      const error = new Error(`Storage returned HTTP ${xhr.status}`);
      error.status = xhr.status;
      settle(reject, error);
    }
  };
  xhr.onerror = () => settle(reject, new Error('Network error while uploading'));
  xhr.onabort = () => {
    const error = new Error(xhr.pausedAbort ? 'Upload paused' : stalled ? 'Upload stalled: no data sent for 60 seconds' : 'Upload aborted');
    error.paused = !!xhr.pausedAbort;
    settle(reject, error);
  };
  xhr.send(body);
});

// Runs tasks with at most `concurrency` at once. After a task fails no new ones start, the ones already
// running are allowed to finish, and then the first error is thrown. Rejecting straight away would leave
// the other workers uploading in the background (e.g. more parts of a file already marked FAILED).
export const runPool = async (tasks, concurrency) => {
  let cursor = 0;
  let firstError = null;
  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, async () => {
    while (cursor < tasks.length && !firstError) {
      const task = tasks[cursor++];
      try {
        await task();
      } catch (error) {
        if (!firstError) firstError = error;
      }
    }
  });
  await Promise.all(workers);
  if (firstError) throw firstError;
};

const chunk = (list, size) => {
  const chunks = [];
  for (let i = 0; i < list.length; i += size) chunks.push(list.slice(i, i + size));
  return chunks;
};

const toFileMeta = ({ file, relativePath }) => ({
  name: file.name,
  size: file.size,
  type: file.type || 'application/octet-stream',
  path: relativePath,
});

/**
 * Uploads items ({ file, relativePath, restart? }) into an existing upload session.
 * restart: true marks a file that changed since it was registered (different size or lastModified);
 * its uploaded data is discarded first so old and new bytes are never mixed.
 * Files under 100 MB go up in one PUT; larger files go up in parts (server decides via upload_type).
 * Re-running it for the same upload_id is safe: the server matches files by path, completed files
 * are skipped, failed ones are reset to PENDING, and large files continue from parts_done.
 * Never rejects: per-file failures are collected and returned so one bad file
 * doesn't stop the rest of the upload.
 * Only one tab uploads a session at a time: if another tab is already uploading it, nothing is sent
 * and the result has alreadyRunning: true.
 */
export const runStreamUpload = (params) => withUploadLock(params.uploadId, () => uploadSession(params), () => ({
  totalFiles: params.items.length,
  totalBytes: params.items.reduce((sum, item) => sum + item.file.size, 0),
  completedFiles: 0,
  failedFiles: 0,
  skippedFiles: 0,
  uploadedBytes: 0,
  failures: [],
  skipped: [],
  alreadyRunning: true,
}));

const uploadSession = async ({ assetType, assetSlug, uploadId, items }) => {
  const api = { assetType, assetSlug, uploadId };
  const control = createControl(uploadId);
  const retry = (task) => withRetry(task, control);
  const progress = {
    totalFiles: items.length,
    totalBytes: items.reduce((sum, item) => sum + item.file.size, 0),
    completedFiles: 0,
    failedFiles: 0,
    skippedFiles: 0,
    uploadedBytes: 0,
  };
  const failures = [];
  const skipped = [];
  // Bytes sent so far for files still in flight, so the bar moves within large files
  const inFlight = new Map();
  // The file behind each in-flight fileId, so the files panel can list what is uploading right now
  const inFlightItems = new Map();
  let speed = 0;
  let speedSample = { at: Date.now(), bytes: 0 };
  let lastPublish = 0;

  const snapshot = () => {
    let inFlightBytes = 0;
    inFlight.forEach(bytes => { inFlightBytes += bytes; });
    // activeFiles: what is being sent right now; failures: every failed file with its reason (the same
    // array the result returns, shared rather than copied on every progress update)
    const activeFiles = [];
    inFlightItems.forEach((item, fileId) => activeFiles.push({
      fileId, path: item.relativePath, size: item.file.size, bytes: inFlight.get(fileId) || 0,
    }));
    return { ...progress, uploadedBytes: progress.uploadedBytes + inFlightBytes, activeFiles, failures };
  };
  const publish = (force = false) => {
    const now = Date.now();
    if (!force && now - lastPublish < PUBLISH_INTERVAL_MS) return;
    lastPublish = now;
    const current = snapshot();
    if (now - speedSample.at >= SPEED_SAMPLE_MS) {
      const instant = Math.max(0, current.uploadedBytes - speedSample.bytes) / ((now - speedSample.at) / 1000);
      speed = speed ? speed * 0.7 + instant * 0.3 : instant;
      speedSample = { at: now, bytes: current.uploadedBytes };
    }
    updateLiveUpload(uploadId, { ...current, speed: control.paused ? 0 : speed, status: control.paused ? LIVE_STATUS.PAUSED : LIVE_STATUS.UPLOADING });
  };
  const fail = (item, reason) => {
    progress.failedFiles += 1;
    failures.push({ path: item.relativePath, size: item.file.size, error: reason || 'Upload failed' });
  };
  const complete = (item) => {
    progress.completedFiles += 1;
    progress.uploadedBytes += item.file.size;
  };

  const fetchFileInfo = async (fileId) => {
    const fresh = await getUploadFileUrlsApi({ ...api, fileIds: [fileId] });
    if (!fresh.urls[fileId]) throw new Error('No upload URL returned');
    return fresh.urls[fileId];
  };

  // Whole file in one PUT, with the server's headers (the URL signs content-type).
  // Every retry gets a fresh URL.
  const uploadSingle = async (fileId, item, initial) => {
    let signed = initial;
    await retry(async (attempt) => {
      if (attempt > 0 || isExpiring(signed)) signed = await fetchFileInfo(fileId);
      inFlight.set(fileId, 0);
      try {
        await putToStorage({ url: signed.url, method: signed.method, headers: signed.headers || {} }, item.file, (bytes) => {
          inFlight.set(fileId, bytes);
          publish();
        }, control);
      } catch (error) {
        inFlight.set(fileId, 0);
        publish();
        throw error;
      }
    });
  };

  // Uploads the given part numbers of a multipart file. Parts already on the server count as sent.
  // Part n = file.slice((n - 1) * partSize, n * partSize), PUT with no headers.
  const uploadParts = async (fileId, item, info, partNumbers, partBytes) => {
    const { partSize } = info;
    const publishFile = () => {
      let sent = 0;
      partBytes.forEach(bytes => { sent += bytes; });
      inFlight.set(fileId, sent);
      publish();
    };
    partNumbers.forEach(n => partBytes.set(n, 0));
    publishFile();

    for (const group of chunk(partNumbers, PART_SIGN_BATCH)) {
      let urls = await retry(() => getUploadPartUrlsApi({ ...api, fileId, partNumbers: group }));
      await runPool(group.map(n => async () => {
        const blob = item.file.slice((n - 1) * partSize, n * partSize);
        await retry(async (attempt) => {
          // Every retry, a missing URL, or one close to expiry gets a freshly signed URL
          if (attempt > 0 || !urls[n] || isExpiring(urls[n])) {
            urls = { ...urls, ...(await getUploadPartUrlsApi({ ...api, fileId, partNumbers: [n] })) };
            if (!urls[n]) throw new Error(`No upload URL returned for part ${n}`);
          }
          try {
            await putToStorage({ url: urls[n].url }, blob, (bytes) => {
              partBytes.set(n, bytes);
              publishFile();
            }, control);
            partBytes.set(n, partSizeOf(item.file, partSize, n));
            publishFile();
          } catch (error) {
            partBytes.set(n, 0);
            publishFile();
            const partError = new Error(`Part ${n} failed: ${error.message}`);
            partError.paused = error.paused;
            throw partError;
          }
        });
      }), PART_CONCURRENCY);
    }
  };

  const uploadMultipart = async (fileId, item, info, partBytes) => {
    const done = new Set(info.partsDone);
    done.forEach(n => partBytes.set(n, partSizeOf(item.file, info.partSize, n)));
    const todo = [];
    for (let n = 1; n <= info.totalParts; n++) if (!done.has(n)) todo.push(n);
    await uploadParts(fileId, item, info, todo, partBytes);
  };

  // COMPLETED is safe to send twice. For multipart files the server may answer with
  // missing_parts; those are uploaded again and COMPLETED is re-sent.
  const markCompleted = async (fileId, item, info, partBytes) => {
    for (let round = 0; round < MAX_COMPLETE_ROUNDS; round++) {
      let missing = [];
      try {
        const data = await retry(() => updateUploadFileStatusApi({ ...api, fileId, status: 'COMPLETED' }));
        missing = data && Array.isArray(data.missing_parts) ? data.missing_parts.map(Number) : [];
        if (!missing.length) return;
      } catch (error) {
        missing = missingPartsOf(error);
        if (!missing.length) throw error;
      }
      if (info.uploadType !== UPLOAD_TYPE.MULTIPART) throw new Error('Server reported missing parts for a single-part file');
      await uploadParts(fileId, item, info, missing, partBytes);
    }
    throw new Error('Some parts are still missing after re-uploading them');
  };

  const uploadOne = async (fileId, item, initialInfo) => {
    let stage = 'upload';
    inFlightItems.set(fileId, item);
    try {
      const info = initialInfo || await retry(() => fetchFileInfo(fileId));
      const partBytes = new Map();
      if (info.uploadType === UPLOAD_TYPE.MULTIPART) await uploadMultipart(fileId, item, info, partBytes);
      else await uploadSingle(fileId, item, info);
      // The bytes are in storage, but the file only appears in the folder once the server
      // marks it completed, so it isn't counted as done until that call succeeds
      stage = 'complete';
      await markCompleted(fileId, item, info, partBytes);
      inFlight.delete(fileId);
      inFlightItems.delete(fileId);
      complete(item);
    } catch (error) {
      inFlight.delete(fileId);
      inFlightItems.delete(fileId);
      if (stage === 'complete') {
        fail(item, `Uploaded, but the server could not be updated: ${error.message}`);
      } else {
        fail(item, error.message);
        // Best effort: the file already counts as failed locally even if this report is lost
        updateUploadFileStatusApi({ ...api, fileId, status: 'FAILED', errorMessage: error.message }).catch(() => {});
      }
    }
    publish();
  };

  publish(true);
  // Progress is otherwise only published when bytes move, so a stalled or offline upload would keep
  // showing its last speed; ticking lets the speed fall to 0 when nothing is being sent
  const ticker = setInterval(() => publish(true), 2000);
  // A missed heartbeat is harmless; the next one (or the file finishing) refreshes updated_at
  const heartbeat = setInterval(() => {
    inFlight.forEach((bytes, fileId) => {
      reportUploadFileProgressApi({ ...api, fileId, uploadedBytes: bytes }).catch(() => {});
    });
  }, HEARTBEAT_INTERVAL_MS);
  try {
    for (let offset = 0; offset < items.length; offset += BATCH_SIZE) {
      const batch = items.slice(offset, offset + BATCH_SIZE);

      let registered;
      try {
        registered = await retry(() => registerUploadFilesApi({ ...api, files: batch.map(toFileMeta) }));
      } catch (error) {
        batch.forEach(item => fail(item, error.message));
        publish();
        continue;
      }

      // Match server rows back to local files by path, or by name when it is unique in the batch. The
      // server stores paths normalized (e.g. double spaces collapsed), so both sides are normalized.
      const byPath = new Map(registered.files.filter(row => row.path).map(row => [normalizeUploadPath(row.path), row]));
      const nameCounts = new Map();
      batch.forEach(item => nameCounts.set(item.file.name, (nameCounts.get(item.file.name) || 0) + 1));
      const byName = new Map(registered.files.filter(row => row.name && nameCounts.get(row.name) === 1).map(row => [row.name, row]));
      const rejected = new Map(registered.rejected.filter(entry => entry.id).map(entry => [normalizeUploadPath(entry.id), entry.reason]));

      const toUpload = [];
      batch.forEach(item => {
        const row = byPath.get(normalizeUploadPath(item.relativePath)) || byName.get(item.file.name);
        if (row && row.fileId) {
          if (DONE_STATUSES.includes(row.status)) {
            complete(item);
          } else {
            // A failed file is reset to PENDING, and a changed one restarted, before it is signed again
            toUpload.push({ item, fileId: row.fileId, resetFailed: row.status === 'FAILED', restart: !!item.restart });
          }
        } else {
          const reason = rejected.get(normalizeUploadPath(item.relativePath)) || rejected.get(normalizeUploadPath(item.file.name));
          fail(item, reason ? `Rejected: ${reason}` : rejected.size ? 'Rejected by server' : 'File was not registered');
        }
      });
      publish();

      const ready = [];
      await runPool(toUpload.map(pair => async () => {
        try {
          if (pair.resetFailed) await retry(() => updateUploadFileStatusApi({ ...api, fileId: pair.fileId, status: 'PENDING' }));
          if (pair.restart) await retry(() => restartUploadFileApi({ ...api, fileId: pair.fileId, fileSize: pair.item.file.size }));
          ready.push(pair);
        } catch (error) {
          fail(pair.item, `Could not reset the file for upload: ${error.message}`);
        }
      }), UPLOAD_CONCURRENCY);
      publish();
      if (!ready.length) continue;

      let signedBatch;
      try {
        signedBatch = await retry(() => getUploadFileUrlsApi({ ...api, fileIds: ready.map(pair => pair.fileId) }));
      } catch (error) {
        ready.forEach(pair => fail(pair.item, error.message));
        publish();
        continue;
      }
      const skippedIds = new Map(signedBatch.skipped.filter(entry => entry.id).map(entry => [entry.id, entry.reason]));

      await runPool(ready.map(({ item, fileId }) => async () => {
        if (skippedIds.has(fileId)) {
          progress.skippedFiles += 1;
          skipped.push({ path: item.relativePath, reason: skippedIds.get(fileId) || 'Skipped by server' });
          publish();
          return;
        }
        await uploadOne(fileId, item, signedBatch.urls[fileId]);
      }), UPLOAD_CONCURRENCY);
    }
  } finally {
    clearInterval(heartbeat);
    clearInterval(ticker);
    controls.delete(uploadId);
    updateLiveUpload(uploadId, { ...snapshot(), speed: 0, status: LIVE_STATUS.DONE });
  }
  return { ...progress, failures, skipped };
};
